import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '@auth';
import {
  connectWiseOpportunityUrl,
  connectWiseTicketUrl,
  fetchConnectWiseLinkConfig,
  type ConnectWiseLinkConfig,
} from './connectWiseLinkService';

export interface ConnectWiseLinks {
  ready: boolean;
  ticketUrl: (recId: string | number) => string;
  opportunityUrl: (recId: string | number) => string;
  loginUrl: string;
}

const emptyLinks: ConnectWiseLinks = {
  ready: false,
  ticketUrl: () => '#',
  opportunityUrl: () => '#',
  loginUrl: '',
};

const ConnectWiseLinkContext = createContext<ConnectWiseLinks>(emptyLinks);

interface ConnectWiseLinkProviderProps {
  children: ReactNode;
}

export function ConnectWiseLinkProvider({ children }: ConnectWiseLinkProviderProps) {
  const { isAuthenticated } = useAuth();
  const [config, setConfig] = useState<ConnectWiseLinkConfig | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setConfig(null);
      return;
    }

    let cancelled = false;

    fetchConnectWiseLinkConfig()
      .then((loaded) => {
        if (!cancelled) setConfig(loaded);
      })
      .catch((error) => {
        console.error('Failed to load ConnectWise link config:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const value = useMemo<ConnectWiseLinks>(() => {
    if (!config?.loginUrl) return emptyLinks;

    return {
      ready: true,
      ticketUrl: (recId) => connectWiseTicketUrl(config, recId),
      opportunityUrl: (recId) => connectWiseOpportunityUrl(config, recId),
      loginUrl: config.loginUrl,
    };
  }, [config]);

  return (
    <ConnectWiseLinkContext.Provider value={value}>
      {children}
    </ConnectWiseLinkContext.Provider>
  );
}

export function useConnectWiseLinks(): ConnectWiseLinks {
  return useContext(ConnectWiseLinkContext);
}
